import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Divider,
  Stack,
  IconButton,
  Chip,
  Paper,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BusinessIcon from "@mui/icons-material/Business";
import WarningIcon from "@mui/icons-material/Warning";

import { verifyVendor, rejectVendor } from "../services/api";

const VendorVerificationDialog = ({ open, onClose, vendor, onDone }) => {
  const [mode, setMode] = useState("review"); // "review" | "verify" | "reject"
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!vendor) return null;

  const resetState = () => {
    setMode("review");
    setNotes("");
    setReason("");
    setError("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleVerify = async () => {
    setSubmitting(true);
    setError("");
    try {
      await verifyVendor(vendor.vendorId, notes.trim() || null);
      onDone(`Vendor "${vendor.vendorName}" verified successfully`);
      resetState();
    } catch (err) {
      setError(err.response?.data?.message || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      setError("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await rejectVendor(vendor.vendorId, reason.trim());
      onDone(`Vendor "${vendor.vendorName}" rejected`);
      resetState();
    } catch (err) {
      setError(err.response?.data?.message || "Rejection failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Document row component
  const DocumentRow = ({ label, url, required = false }) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      py={1}
      px={1.5}
      sx={{
        bgcolor: url ? "success.50" : required ? "error.50" : "grey.50",
        borderRadius: 1,
        border: "1px solid",
        borderColor: url ? "success.light" : required ? "error.light" : "divider",
        mb: 1,
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <DescriptionIcon
          sx={{
            fontSize: 18,
            color: url ? "success.main" : required ? "error.main" : "text.secondary",
          }}
        />
        <Typography variant="body2" fontWeight={500}>
          {label}
          {required && (
            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
      </Box>
      {url ? (
        <Link
          href={url}
          target="_blank"
          rel="noopener"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: "0.85rem",
            textDecoration: "none",
          }}
        >
          View Document <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      ) : (
        <Typography variant="caption" color={required ? "error" : "text.secondary"}>
          {required ? "Missing — required" : "Not uploaded"}
        </Typography>
      )}
    </Box>
  );

  // Section label
  const SectionLabel = ({ children }) => (
    <Typography
      variant="caption"
      color="text.secondary"
      fontWeight={600}
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.5,
        mb: 1,
        display: "block",
      }}
    >
      {children}
    </Typography>
  );

  // Header colors based on current status
  const getHeaderColor = () => {
    switch (vendor.verificationStatus) {
      case "VERIFIED":
        return "success.main";
      case "REJECTED":
        return "error.main";
      default:
        return "warning.main";
    }
  };

  const getStatusChip = () => {
    switch (vendor.verificationStatus) {
      case "VERIFIED":
        return <Chip icon={<CheckCircleIcon />} label="Verified" color="success" size="small" />;
      case "REJECTED":
        return <Chip icon={<CancelIcon />} label="Rejected" color="error" size="small" />;
      default:
        return <Chip label="Pending Review" color="warning" size="small" />;
    }
  };

  // FLEET vendors require GST and Business License
  const isFleet = vendor.vendorType === "FLEET";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Header */}
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: getHeaderColor(),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <VerifiedUserIcon />
          </Box>
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold">
              {mode === "review" && "Review Vendor Documents"}
              {mode === "verify" && "Approve Vendor"}
              {mode === "reject" && "Reject Vendor"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Typography variant="caption" color="text.secondary">
                Vendor ID: #{vendor.vendorId}
              </Typography>
              {getStatusChip()}
            </Box>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Show rejection reason if rejected */}
        {vendor.verificationStatus === "REJECTED" && vendor.rejectionReason && (
          <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Previously rejected:
            </Typography>
            <Typography variant="body2">{vendor.rejectionReason}</Typography>
          </Alert>
        )}

        {/* Show verification notes if verified */}
        {vendor.verificationStatus === "VERIFIED" && vendor.verificationNotes && (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600}>
              Verification notes:
            </Typography>
            <Typography variant="body2">{vendor.verificationNotes}</Typography>
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Vendor info */}
          <Box>
            <SectionLabel>Vendor Information</SectionLabel>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <StorefrontIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" fontWeight={600}>
                    {vendor.vendorName}
                  </Typography>
                  <Chip label={vendor.vendorType} size="small" variant="outlined" />
                  <Chip label={vendor.category} size="small" color="primary" variant="outlined" />
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2">
                    {vendor.contactPerson}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2">{vendor.contactNumber}</Typography>
                </Box>
                {vendor.email && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2">{vendor.email}</Typography>
                  </Box>
                )}
                {vendor.address && (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <BusinessIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.3 }} />
                    <Typography variant="body2">{vendor.address}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Business identity */}
          <Box>
            <SectionLabel>Business Identity</SectionLabel>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    PAN Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {vendor.panNumber || "—"}
                  </Typography>
                </Box>
                {(isFleet || vendor.gstNumber) && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      GST Number {isFleet && "*"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.gstNumber || "—"}
                    </Typography>
                  </Box>
                )}
                {(isFleet || vendor.businessLicenseNumber) && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Business License # {isFleet && "*"}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.businessLicenseNumber || "—"}
                    </Typography>
                  </Box>
                )}
                {vendor.vehicleNumber && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Vehicle Number
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.vehicleNumber}
                    </Typography>
                  </Box>
                )}
                {vendor.licenseNumber && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      License Number
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.licenseNumber}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Documents */}
          <Box>
            <SectionLabel>Uploaded Documents</SectionLabel>
            <DocumentRow
              label="Address Proof"
              url={vendor.addressProofUrl}
              required={true}
            />
            <DocumentRow
              label="PAN Card"
              url={vendor.panCardUrl}
              required={true}
            />
            <DocumentRow
              label="GST Certificate"
              url={vendor.gstCertificateUrl}
              required={isFleet}
            />
            <DocumentRow
              label="Business License"
              url={vendor.businessLicenseUrl}
              required={isFleet}
            />
            <DocumentRow
              label="Service Agreement"
              url={vendor.agreementDocumentUrl}
              required={false}
            />
          </Box>

          {/* Bank details */}
          <Box>
            <SectionLabel>Bank Details</SectionLabel>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              {vendor.bankAccountNumber || vendor.bankIfscCode ? (
                <Stack spacing={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccountBalanceIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.bankAccountHolderName || "—"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Account #
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.bankAccountNumber || "—"}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      IFSC Code
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {vendor.bankIfscCode || "—"}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No bank details provided
                </Typography>
              )}
            </Paper>
          </Box>

          {/* Action mode: verify */}
          {mode === "verify" && (
            <Box>
              <SectionLabel>Verification Notes (Optional)</SectionLabel>
              <TextField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Add any notes about this verification..."
              />
            </Box>
          )}

          {/* Action mode: reject */}
          {mode === "reject" && (
            <Box>
              <SectionLabel>Rejection Reason (Required)</SectionLabel>
              <TextField
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                  setError("");
                }}
                fullWidth
                multiline
                rows={4}
                required
                placeholder="Explain why this vendor is being rejected (e.g., 'PAN card image is unclear', 'GST certificate expired', etc.)"
                error={error.length > 0 && !reason.trim()}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                The vendor will see this reason. Be specific so they can correct the issue.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {mode === "review" ? (
          <>
            <Button onClick={handleClose}>Close</Button>
            {vendor.verificationStatus !== "REJECTED" && (
              <Button
                onClick={() => setMode("reject")}
                color="error"
                variant="outlined"
                startIcon={<CancelIcon />}
              >
                Reject
              </Button>
            )}
            {vendor.verificationStatus !== "VERIFIED" && (
              <Button
                onClick={() => setMode("verify")}
                color="success"
                variant="contained"
                startIcon={<CheckCircleIcon />}
              >
                Approve
              </Button>
            )}
          </>
        ) : mode === "verify" ? (
          <>
            <Button onClick={() => setMode("review")} disabled={submitting}>
              Back
            </Button>
            <Button
              onClick={handleVerify}
              variant="contained"
              color="success"
              disabled={submitting}
              startIcon={<CheckCircleIcon />}
            >
              {submitting ? "Verifying..." : "Confirm Approval"}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setMode("review")} disabled={submitting}>
              Back
            </Button>
            <Button
              onClick={handleReject}
              variant="contained"
              color="error"
              disabled={submitting || !reason.trim()}
              startIcon={<CancelIcon />}
            >
              {submitting ? "Rejecting..." : "Confirm Rejection"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VendorVerificationDialog;